import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, DollarSign, Upload, FileText, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function ContaReceitaZendry() {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [saldoClientesDiversos, setSaldoClientesDiversos] = useState('');
  const [isEditingSaldo, setIsEditingSaldo] = useState(false);
  const [tipoTransacao, setTipoTransacao] = useState('credito'); // 'credito' ou 'debito'
  const [bancoSelecionado, setBancoSelecionado] = useState('primepag'); // 'primepag' ou 'c6bank'
  const [ofxData, setOfxData] = useState(null);
  const [contabilData, setContabilData] = useState(null);
  const [conciliacaoResultado, setConciliacaoResultado] = useState(null);
  const [ofxPrevioData, setOfxPrevioData] = useState(null);
  const [csvPrevioData, setCsvPrevioData] = useState(null);
  const [conciliacaoPrevia, setConciliacaoPrevia] = useState(null);
  const fileInputRef = useRef(null);
  const ofxInputRef = useRef(null);
  const contabilInputRef = useRef(null);
  const ofxPrevioInputRef = useRef(null);
  const csvPrevioInputRef = useRef(null);

  // Função para processar o valor monetário
  const parseValue = (value) => {
    if (!value || value === '---') return 0;
    // Remove espaços e converte vírgula para ponto
    const cleanValue = value.toString().replace(/\s/g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  const handleOFXPrevioUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarOFXPrevio(e.target.result);
      } catch (error) {
        console.error('Erro ao processar OFX prévio:', error);
        alert('Erro ao processar o arquivo OFX. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file);
  };

  const copiarListaDivergencias = (itens, tipo) => {
    const texto = itens
      .map((item) => {
        const valor = (item.valorAbs ?? item.valor ?? 0).toFixed(2);
        const descricao = tipo === 'ofx' ? item.memo : item.historico;
        return `${item.data};${valor};${descricao || ''}`;
      })
      .join('\n');

    navigator.clipboard.writeText(texto)
      .catch(() => undefined);
  };

  const copiarDivergencias = (tipo) => {
    if (!conciliacaoResultado) return;
    const itens = tipo === 'ofx'
      ? conciliacaoResultado.somenteOFX
      : conciliacaoResultado.somenteContabil;
    copiarListaDivergencias(itens, tipo);
  };

  const copiarDivergenciasPrevias = (tipo) => {
    if (!conciliacaoPrevia) return;
    const itens = tipo === 'ofx'
      ? conciliacaoPrevia.somenteOFX
      : conciliacaoPrevia.somenteContabil;
    copiarListaDivergencias(itens, tipo);
  };

  const parseValorBR = (value) => {
    if (!value || value === '---') return 0;
    const cleanValue = value.toString().replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Função para processar o CSV
  const processCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const headers = lines[0].split(';');
    const data = [];
    
    let totalCredito = 0;
    let totalDebito = 0;
    let saldoFinal = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 8) {
        const row = {
          descricao: values[0],
          referencia1: values[1],
          referencia2: values[2],
          referencia3: values[3],
          creditado: values[4],
          debitado: values[5],
          data: values[6],
          valor: parseValue(values[7]),
          saldo: parseValue(values[8])
        };
        
        data.push(row);
        
        // Log das primeiras 5 transações para debug
        if (i <= 5) {
          console.log(`📝 Transação ${i} (CSV):`, {
            descricao: row.descricao,
            valor: row.valor,
            valorOriginal: values[7],
            saldo: row.saldo,
            saldoOriginal: values[8]
          });
        }
        
        // Calcular totais
        if (row.valor > 0) {
          totalCredito += row.valor;
        } else {
          totalDebito += Math.abs(row.valor);
        }
        
        // O saldo final é o último saldo do arquivo
        saldoFinal = row.saldo;
      }
    }

    return {
      data,
      summary: {
        totalCredito,
        totalDebito,
        saldoFinal,
        totalTransacoes: data.length
      }
    };
  };

  // Função para lidar com o upload do arquivo
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para calcular valor dos lançamentos em Clientes Diversos (código 142)
  // com lógica otimizada para maximizar o uso do limite
  const calcularClientesDiversos = () => {
    if (!csvData.length || !saldoClientesDiversos) {
      return 0;
    }
    
    const saldoLimite = converterValorParaNumero(saldoClientesDiversos);
    const transacoesCredito = csvData.filter(row => row.valor > 0);
    const totalCreditos = transacoesCredito.reduce((sum, row) => sum + row.valor, 0);
    
    console.log('🔍 CLIENTES DIVERSOS - DIAGNÓSTICO OTIMIZADO:');
    console.log('💰 Limite informado:', saldoLimite);
    console.log('💰 Total de créditos no arquivo:', totalCreditos);
    console.log('📊 Quantidade de transações de crédito:', transacoesCredito.length);
    
    if (totalCreditos <= saldoLimite) {
      console.log('✅ TODO O VALOR CABE NO LIMITE - Usando tudo como código 142');
      console.log('💰 Valor para código 142:', totalCreditos);
      console.log('💰 Valor para código 2703: 0');
      return totalCreditos;
    }
    
    console.log('⚠️ TOTAL ULTRAPASSA O LIMITE - Aplicando lógica otimizada');
    
    // Array para marcar quais transações serão código 142
    const transacoesUsadas = new Array(transacoesCredito.length).fill(false);
    let valorAcumulado = 0;
    let transacoesIncluidas = 0;
    
    // Primeira passada: incluir transações sequenciais que cabem
    console.log('🔄 PRIMEIRA PASSADA - Transações sequenciais:');
    for (let i = 0; i < transacoesCredito.length; i++) {
      const transacao = transacoesCredito[i];
      if (valorAcumulado + transacao.valor <= saldoLimite) {
        transacoesUsadas[i] = true;
        valorAcumulado += transacao.valor;
        transacoesIncluidas++;
        console.log(`✅ Transação ${i + 1}: R$ ${transacao.valor.toFixed(2)} - Acumulado: R$ ${valorAcumulado.toFixed(2)}`);
      } else {
        console.log(`❌ Transação ${i + 1}: R$ ${transacao.valor.toFixed(2)} - Ultrapassaria o limite`);
        break;
      }
    }
    
    console.log(`📊 Após primeira passada: ${transacoesIncluidas} transações, R$ ${valorAcumulado.toFixed(2)}`);
    console.log(`💰 Espaço restante no limite: R$ ${(saldoLimite - valorAcumulado).toFixed(2)}`);
    
    // Segunda passada: verificar transações posteriores que ainda cabem
    console.log('🔄 SEGUNDA PASSADA - Buscando transações que cabem no espaço restante:');
    let transacoesAdicionais = 0;
    
    for (let i = transacoesIncluidas; i < transacoesCredito.length; i++) {
      if (transacoesUsadas[i]) continue; // Já foi incluída
      
      const transacao = transacoesCredito[i];
      const espacoRestante = saldoLimite - valorAcumulado;
      
      if (transacao.valor <= espacoRestante) {
        transacoesUsadas[i] = true;
        valorAcumulado += transacao.valor;
        transacoesAdicionais++;
        console.log(`✅ Transação adicional ${i + 1}: R$ ${transacao.valor.toFixed(2)} - Novo acumulado: R$ ${valorAcumulado.toFixed(2)}`);
        console.log(`💰 Espaço restante: R$ ${(saldoLimite - valorAcumulado).toFixed(2)}`);
      } else {
        console.log(`❌ Transação ${i + 1}: R$ ${transacao.valor.toFixed(2)} - Não cabe no espaço restante (R$ ${espacoRestante.toFixed(2)})`);
      }
    }
    
    console.log('🏁 RESULTADO OTIMIZADO:');
    console.log('💰 Valor para código 142:', valorAcumulado);
    console.log('💰 Valor para código 2703:', totalCreditos - valorAcumulado);
    console.log('📊 Transações sequenciais incluídas:', transacoesIncluidas);
    console.log('📊 Transações adicionais encontradas:', transacoesAdicionais);
    console.log('📊 Total de transações código 142:', transacoesIncluidas + transacoesAdicionais);
    console.log('💰 Aproveitamento do limite:', ((valorAcumulado / saldoLimite) * 100).toFixed(2) + '%');
    
    return valorAcumulado;
  };



  // Função para baixar JSON dos dados
  const downloadJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conta-receita-zendry-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para copiar JSON
  const copyJSON = () => {
    const dataToDownload = {
      summary,
      transactions: csvData,
      processedAt: new Date().toISOString()
    };
    
    navigator.clipboard.writeText(JSON.stringify(dataToDownload, null, 2))
      .then(() => alert('JSON copiado para a área de transferência!'))
      .catch(() => alert('Erro ao copiar JSON'));
  };

  // Função para formatar valor monetário no campo de entrada
  const formatarValorMonetario = (valor) => {
    if (!valor) return '';
    
    // Remove tudo que não é dígito
    const numeroLimpo = valor.replace(/\D/g, '');
    
    // Converte para número e divide por 100 para ter as casas decimais
    const numero = parseFloat(numeroLimpo) / 100;
    
    // Formata com separadores de milhar e decimal
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter valor formatado para número
  const converterValorParaNumero = (valorFormatado) => {
    console.log('🔄 converterValorParaNumero - Input:', valorFormatado);
    
    if (!valorFormatado) {
      console.log('🔄 converterValorParaNumero - Valor vazio, retornando 0');
      return 0;
    }
    
    // Remove pontos (separadores de milhar) e substitui vírgula por ponto
    const valorLimpo = valorFormatado
      .replace(/\./g, '')
      .replace(',', '.');
    
    const resultado = parseFloat(valorLimpo) || 0;
    console.log('🔄 converterValorParaNumero - Valor limpo:', valorLimpo);
    console.log('🔄 converterValorParaNumero - Resultado:', resultado);
    
    return resultado;
  };

  // Função para extrair ID da transação da descrição
  const extrairIdTransacao = (descricao) => {
    const match = descricao.match(/#(\d+)/);
    return match ? match[1] : 'N/A';
  };

  const normalizarDataParaChave = (data) => {
    if (!data) return '';
    const matchBr = data.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (matchBr) {
      return `${matchBr[3]}${matchBr[2]}${matchBr[1]}`;
    }
    const matchOfx = data.match(/(\d{4})(\d{2})(\d{2})/);
    if (matchOfx) {
      return `${matchOfx[1]}${matchOfx[2]}${matchOfx[3]}`;
    }
    return data;
  };

  const processarOFX = (conteudo) => {
    const transacoes = [];
    const regex = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/g;
    let match;

    while ((match = regex.exec(conteudo)) !== null) {
      const transacao = match[1];
      const valorMatch = transacao.match(/<TRNAMT>([-\d.]+)/);
      const dataMatch = transacao.match(/<DTPOSTED>(\d{8})/);
      const tipoMatch = transacao.match(/<TRNTYPE>(\w+)</);
      const memoMatch = transacao.match(/<MEMO>([^\r\n<]*)/);

      if (valorMatch && dataMatch) {
        const valorBruto = parseFloat(valorMatch[1]);
        const tipo = tipoMatch ? tipoMatch[1] : '';
        const memo = memoMatch ? memoMatch[1].trim() : '';
        const dataStr = dataMatch[1];
        const dataFormatada = `${dataStr.substring(6, 8)}/${dataStr.substring(4, 6)}/${dataStr.substring(0, 4)}`;
        const valor = tipo === 'DEBIT' && valorBruto > 0 ? -valorBruto : valorBruto;
        const valorAbs = Math.abs(valorBruto);
        const idTransacao = extrairIdTransacao(memo);

        transacoes.push({
          data: dataFormatada,
          dataKey: normalizarDataParaChave(dataFormatada),
          tipo,
          memo,
          idTransacao,
          valor,
          valorAbs
        });
      }
    }

    const totalValor = transacoes.reduce((sum, item) => sum + item.valorAbs, 0);
    setOfxData({ transacoes, totalValor });
    return { transacoes, totalValor };
  };

  const processarOFXPrevio = (conteudo) => {
    const transacoes = [];
    const regex = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/g;
    let match;

    while ((match = regex.exec(conteudo)) !== null) {
      const transacao = match[1];
      const valorMatch = transacao.match(/<TRNAMT>([-\d.]+)/);
      const dataMatch = transacao.match(/<DTPOSTED>(\d{8})/);
      const tipoMatch = transacao.match(/<TRNTYPE>(\w+)</);
      const memoMatch = transacao.match(/<MEMO>([^\r\n<]*)/);

      if (valorMatch && dataMatch) {
        const valorBruto = parseFloat(valorMatch[1]);
        const tipo = tipoMatch ? tipoMatch[1] : '';
        const memo = memoMatch ? memoMatch[1].trim() : '';
        const dataStr = dataMatch[1];
        const dataFormatada = `${dataStr.substring(6, 8)}/${dataStr.substring(4, 6)}/${dataStr.substring(0, 4)}`;
        const valor = tipo === 'DEBIT' && valorBruto > 0 ? -valorBruto : valorBruto;
        const valorAbs = Math.abs(valorBruto);
        const idTransacao = extrairIdTransacao(memo);

        transacoes.push({
          data: dataFormatada,
          dataKey: normalizarDataParaChave(dataFormatada),
          tipo,
          memo,
          idTransacao,
          valor,
          valorAbs
        });
      }
    }

    const totalValor = transacoes.reduce((sum, item) => sum + item.valorAbs, 0);
    setOfxPrevioData({ transacoes, totalValor });
    return { transacoes, totalValor };
  };

  const processarCSVPrevio = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 9) {
        const descricao = values[0] || '';
        const dataMovimento = values[6] || '';
        const valor = parseValue(values[7]);
        const dataMatch = dataMovimento.match(/(\d{2}\/\d{2}\/\d{4})/);
        const dataFormatada = dataMatch ? dataMatch[1] : dataMovimento;
        const idTransacao = extrairIdTransacao(descricao);

        data.push({
          data: dataFormatada,
          dataKey: normalizarDataParaChave(dataFormatada),
          historico: descricao,
          valor,
          valorAbs: Math.abs(valor),
          idTransacao
        });
      }
    }

    const totalValor = data.reduce((sum, item) => sum + item.valorAbs, 0);
    setCsvPrevioData({ transacoes: data, totalValor });
    return { transacoes: data, totalValor };
  };

  const processarCSVContabil = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 6) {
        const dataMovimento = values[0]?.trim();
        const debito = values[2]?.trim();
        const credito = values[3]?.trim();
        const valor = parseValorBR(values[4]);
        const historico = values[5] || '';
        const idTransacao = extrairIdTransacao(historico);

        data.push({
          data: dataMovimento,
          dataKey: normalizarDataParaChave(dataMovimento),
          debito,
          credito,
          valor,
          valorAbs: Math.abs(valor),
          historico,
          idTransacao
        });
      }
    }

    const totalValor = data.reduce((sum, item) => sum + item.valorAbs, 0);
    setContabilData({ transacoes: data, totalValor });
    return { transacoes: data, totalValor };
  };

  const realizarConciliacao = () => {
    if (!ofxData || !contabilData) {
      alert('Faça upload do OFX e do CSV contábil para conciliar.');
      return;
    }

    const contabMapPorId = new Map();
    const contabMapPorValor = new Map();
    contabilData.transacoes.forEach((item, index) => {
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        lista.push(index);
        contabMapPorId.set(chave, lista);
      }
      const chaveValor = `${item.dataKey}|${item.valorAbs}`;
      const listaValor = contabMapPorValor.get(chaveValor) || [];
      listaValor.push(index);
      contabMapPorValor.set(chaveValor, listaValor);
    });

    const usadosContabil = new Set();
    const correspondencias = [];
    const somenteOFX = [];

    ofxData.transacoes.forEach((item) => {
      let indiceMatch = null;
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        while (lista.length) {
          const indice = lista.shift();
          if (!usadosContabil.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch === null) {
        const chaveValor = `${item.dataKey}|${item.valorAbs}`;
        const listaValor = contabMapPorValor.get(chaveValor) || [];
        while (listaValor.length) {
          const indice = listaValor.shift();
          if (!usadosContabil.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch !== null) {
        usadosContabil.add(indiceMatch);
        correspondencias.push({
          ofx: item,
          contabil: contabilData.transacoes[indiceMatch]
        });
      } else {
        somenteOFX.push(item);
      }
    });

    const somenteContabil = contabilData.transacoes.filter((_, index) => !usadosContabil.has(index));
    const diferencaTotal = Math.abs(contabilData.totalValor - ofxData.totalValor);
    const taxaConciliacao = (correspondencias.length / Math.max(ofxData.transacoes.length, contabilData.transacoes.length)) * 100;

    setConciliacaoResultado({
      totalOFX: ofxData.totalValor,
      totalContabil: contabilData.totalValor,
      diferencaTotal,
      correspondencias,
      somenteOFX,
      somenteContabil,
      taxaConciliacao
    });
  };

  const realizarConciliacaoPrevia = () => {
    if (!ofxPrevioData || !csvPrevioData) {
      alert('Faça upload do OFX e do CSV bruto para conciliar.');
      return;
    }

    const contabMapPorId = new Map();
    const contabMapPorValor = new Map();
    csvPrevioData.transacoes.forEach((item, index) => {
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        lista.push(index);
        contabMapPorId.set(chave, lista);
      }
      const chaveValor = `${item.dataKey}|${item.valorAbs}`;
      const listaValor = contabMapPorValor.get(chaveValor) || [];
      listaValor.push(index);
      contabMapPorValor.set(chaveValor, listaValor);
    });

    const usadosCsv = new Set();
    const correspondencias = [];
    const somenteOFX = [];

    ofxPrevioData.transacoes.forEach((item) => {
      let indiceMatch = null;
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        while (lista.length) {
          const indice = lista.shift();
          if (!usadosCsv.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch === null) {
        const chaveValor = `${item.dataKey}|${item.valorAbs}`;
        const listaValor = contabMapPorValor.get(chaveValor) || [];
        while (listaValor.length) {
          const indice = listaValor.shift();
          if (!usadosCsv.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch !== null) {
        usadosCsv.add(indiceMatch);
        correspondencias.push({
          ofx: item,
          contabil: csvPrevioData.transacoes[indiceMatch]
        });
      } else {
        somenteOFX.push(item);
      }
    });

    const somenteContabil = csvPrevioData.transacoes.filter((_, index) => !usadosCsv.has(index));
    const diferencaTotal = Math.abs(csvPrevioData.totalValor - ofxPrevioData.totalValor);
    const taxaConciliacao = (correspondencias.length / Math.max(ofxPrevioData.transacoes.length, csvPrevioData.transacoes.length)) * 100;

    setConciliacaoPrevia({
      totalOFX: ofxPrevioData.totalValor,
      totalContabil: csvPrevioData.totalValor,
      diferencaTotal,
      correspondencias,
      somenteOFX,
      somenteContabil,
      taxaConciliacao
    });
  };

  const handleOFXUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarOFX(e.target.result);
      } catch (error) {
        console.error('Erro ao processar OFX:', error);
        alert('Erro ao processar o arquivo OFX. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file);
  };

  const handleContabilUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarCSVContabil(e.target.result);
      } catch (error) {
        console.error('Erro ao processar CSV contábil:', error);
        alert('Erro ao processar o arquivo CSV contábil. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleCSVPrevioUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarCSVPrevio(e.target.result);
      } catch (error) {
        console.error('Erro ao processar CSV bruto:', error);
        alert('Erro ao processar o arquivo CSV bruto. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    // Exemplo: "07/05/2025 14:55:50" -> "07052025"
    const match = dataOriginal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (match) {
      return `${match[1]}${match[2]}${match[3]}`;
    }
    return '01012024'; // Fallback
  };

  // Função para extrair competência do CSV (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012024'; // Fallback
    }
    
    // Pegar a primeira transação para extrair mês/ano
    const primeiraData = csvData[0].data;
    const match = primeiraData.match(/\d{2}\/(\d{2})\/(\d{4})/);
    
    if (match) {
      const mes = match[1];
      const ano = match[2];
      return `${mes}${ano}`;
    }
    
    return '012024'; // Fallback
  };

  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    return texto
      .normalize('NFD') // Decompor caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos (acentos)
      .toUpperCase(); // Converter para maiúsculo
  };

  // Função para gerar CSV formatado com lógica otimizada
  const gerarCSVFormatado = () => {
    if (!csvData.length) {
      alert('Por favor, faça upload do arquivo CSV.');
      return;
    }

    // Validar saldo (necessário para ambos os tipos, pois débitos também processam créditos)
    if (!saldoClientesDiversos) {
      alert('Por favor, informe o saldo da conta Clientes Diversos.');
      return;
    }

    const linhasCSV = [];

    console.log('📄 GERANDO CSV - Tipo de transação:', tipoTransacao);
    console.log('📄 GERANDO CSV - Banco selecionado:', bancoSelecionado);

    // Processar apenas o tipo de transação selecionado
    if (tipoTransacao === 'credito') {
      // ==================== PROCESSAR CRÉDITOS ====================
      const saldoLimite = converterValorParaNumero(saldoClientesDiversos);
      const transacoesCredito = csvData.filter(row => row.valor > 0);

      console.log('📄 GERANDO CSV - Aplicando lógica otimizada para CRÉDITOS');
      console.log('💰 Limite para código 142:', saldoLimite);
      console.log('📊 Total de transações de crédito:', transacoesCredito.length);

      // Aplicar a mesma lógica otimizada da função calcularClientesDiversos
      const transacoesUsadas = new Array(transacoesCredito.length).fill(false);
      let valorAcumulado = 0;
      let transacoesIncluidas = 0;
    
    // Primeira passada: incluir transações sequenciais que cabem
    for (let i = 0; i < transacoesCredito.length; i++) {
      const transacao = transacoesCredito[i];
      if (valorAcumulado + transacao.valor <= saldoLimite) {
        transacoesUsadas[i] = true;
        valorAcumulado += transacao.valor;
        transacoesIncluidas++;
      } else {
        break;
      }
    }
    
    // Segunda passada: verificar transações posteriores que ainda cabem
    for (let i = transacoesIncluidas; i < transacoesCredito.length; i++) {
      if (transacoesUsadas[i]) continue; // Já foi incluída
      
      const transacao = transacoesCredito[i];
      const espacoRestante = saldoLimite - valorAcumulado;
      
      if (transacao.valor <= espacoRestante) {
        transacoesUsadas[i] = true;
        valorAcumulado += transacao.valor;
      }
    }
    
    console.log('📄 CSV - Transações que usarão código 142:', transacoesUsadas.filter(usado => usado).length);
    console.log('📄 CSV - Valor total código 142:', valorAcumulado);
    
    let virgulasRemovidas = 0;

    // Gerar as linhas do CSV baseado na marcação otimizada
    transacoesCredito.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      const contaDebito = 16;
      const contaCredito = transacoesUsadas[index] ? 142 : 2703;
      const valor = row.valor.toFixed(2); // Manter formato float com ponto decimal
      const zero = 0;
      const idTransacao = extrairIdTransacao(row.descricao);
      const clienteOriginal = row.debitado || 'N/A';
      const clienteDebitado = clienteOriginal.replace(/,/g, ''); // Remover vírgulas do nome
      
      // Contar vírgulas removidas
      if (clienteOriginal !== clienteDebitado) {
        virgulasRemovidas++;
        if (virgulasRemovidas <= 3) {
          console.log(`📄 Vírgula removida do cliente: "${clienteOriginal}" -> "${clienteDebitado}"`);
        }
      }
      
      const descricaoSemAcento = removerAcentosEMaiusculo(`Transacao #${idTransacao} Cliente ${clienteDebitado}`);
      const descricao = `"${descricaoSemAcento}"`; // Adicionar # antes do ID

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5) {
        console.log(`📄 Linha ${index + 1} CSV: Código ${contaCredito}, Valor ${valor}, Descrição: ${descricaoSemAcento}`);
      }
    });

    // COMENTADO: Processar transações de débito antigas (ZENDRY -> ZENDRY S.A) com códigos 18 e 16
    // transacoesDebito.forEach((row, index) => {
    //   const dataFormatada = formatarDataCSV(row.data);
    //   const contaDebito = 18;
    //   const contaCredito = 16;
    //   const valor = Math.abs(row.valor).toFixed(2); // Converter valor negativo para positivo
    //   const zero = 0;
    //   const idTransacao = extrairIdTransacao(row.descricao);
    //   
    //   const descricaoSemAcento = removerAcentosEMaiusculo(`Transacao #${idTransacao} Transferencia Para Contas A Pagar`);
    //   const descricao = `"${descricaoSemAcento}"`;

    //   // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
    //   const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
    //   linhasCSV.push(linha);
    //   
    //   if (index < 3) {
    //     console.log(`📄 Linha DÉBITO ${index + 1} CSV: Débito ${contaDebito}, Crédito ${contaCredito}, Valor ${valor}, Descrição: ${descricaoSemAcento}`);
    //   }
    // });

      console.log('📄 CSV - Transações de crédito processadas:', transacoesCredito.length);
      console.log('📄 - Vírgulas removidas dos nomes:', virgulasRemovidas);

    } else {
      // ==================== PROCESSAR DÉBITOS + CRÉDITOS ====================
      console.log('📄 GERANDO CSV - Processando DÉBITOS + CRÉDITOS');
      console.log('📊 Total de transações no arquivo:', csvData.length);
      console.log('🏦 Banco selecionado:', bancoSelecionado);
      
      // Log detalhado das primeiras transações para debug
      console.log('🔍 Analisando transações:');
      csvData.slice(0, 5).forEach((row, index) => {
        console.log(`  Transação ${index + 1}:`, {
          valor: row.valor,
          debitado: row.debitado,
          creditado: row.creditado,
          descricao: row.descricao?.substring(0, 50)
        });
      });
      
      // PARTE 1: PROCESSAR DÉBITOS
      const transacoesDebitoNovas = csvData.filter(row => {
        const isDebito = row.valor < 0;
        // Verificar se é "Débito em conta" no campo creditado (transferências para contas a pagar)
        const isDebitoEmConta = row.creditado === 'Débito em conta';
        
        if (isDebito) {
          console.log(`🔍 Transação débito - Creditado: "${row.creditado}", Incluir: ${isDebitoEmConta}`);
        }
        
        return isDebito && isDebitoEmConta;
      });

      console.log('📊 Total de transações de débito encontradas:', transacoesDebitoNovas.length);

      transacoesDebitoNovas.forEach((row, index) => {
        const dataFormatada = formatarDataCSV(row.data);
        // Determinar código débito baseado no banco: PrimePag = 18, C6 Bank = 19
        const contaDebito = bancoSelecionado === 'c6bank' ? 19 : 18;
        const contaCredito = 16;
        const valor = Math.abs(row.valor).toFixed(2); // Converter valor negativo para positivo
        const zero = 0;
        const idTransacao = extrairIdTransacao(row.descricao);
        const clienteOriginal = row.debitado || 'N/A';
        const clienteDebitado = clienteOriginal.replace(/,/g, ''); // Remover vírgulas do nome
        
        const descricaoSemAcento = removerAcentosEMaiusculo(`Transacao #${idTransacao} Transferencia Para Contas A Pagar`);
        const descricao = `"${descricaoSemAcento}"`;

        // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
        const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
        linhasCSV.push(linha);
        
        if (index < 3) {
          console.log(`📄 Linha DÉBITO ${index + 1} CSV: Débito ${contaDebito}, Crédito ${contaCredito}, Valor ${valor}`);
        }
      });

      console.log('📄 CSV - Transações de débito processadas:', transacoesDebitoNovas.length);

      // PARTE 2: PROCESSAR CRÉDITOS (com lógica do código 142/2703)
      console.log('📄 Processando também CRÉDITOS para incluir no mesmo CSV...');
      
      const saldoLimite = converterValorParaNumero(saldoClientesDiversos);
      const transacoesCredito = csvData.filter(row => row.valor > 0);

      console.log('💰 Limite para código 142:', saldoLimite);
      console.log('📊 Total de transações de crédito:', transacoesCredito.length);

      // Aplicar a mesma lógica otimizada para créditos
      const transacoesUsadas = new Array(transacoesCredito.length).fill(false);
      let valorAcumulado = 0;
      
      // Primeira passada: incluir transações sequenciais que cabem
      for (let i = 0; i < transacoesCredito.length; i++) {
        const transacao = transacoesCredito[i];
        if (valorAcumulado + transacao.valor <= saldoLimite) {
          transacoesUsadas[i] = true;
          valorAcumulado += transacao.valor;
        } else {
          break;
        }
      }
      
      console.log('📄 CSV - Transações de crédito que usarão código 142:', transacoesUsadas.filter(usado => usado).length);
      
      let virgulasRemovidas = 0;

      // Gerar as linhas do CSV para créditos
      transacoesCredito.forEach((row, index) => {
        const dataFormatada = formatarDataCSV(row.data);
        const contaDebito = 16;
        const contaCredito = transacoesUsadas[index] ? 142 : 2703;
        const valor = row.valor.toFixed(2);
        const zero = 0;
        const idTransacao = extrairIdTransacao(row.descricao);
        const clienteOriginal = row.debitado || 'N/A';
        const clienteDebitado = clienteOriginal.replace(/,/g, '');
        
        if (clienteOriginal !== clienteDebitado) {
          virgulasRemovidas++;
        }
        
        const descricaoSemAcento = removerAcentosEMaiusculo(`Transacao #${idTransacao} Cliente ${clienteDebitado}`);
        const descricao = `"${descricaoSemAcento}"`;

        const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
        linhasCSV.push(linha);
        
        if (index < 3) {
          console.log(`📄 Linha CRÉDITO ${index + 1} CSV: Código ${contaCredito}, Valor ${valor}`);
        }
      });

      console.log('📄 CSV - Transações de crédito processadas:', transacoesCredito.length);
      console.log('📄 - Vírgulas removidas dos nomes:', virgulasRemovidas);
    }

    console.log('📄 CSV gerado com', linhasCSV.length, 'linhas total');

    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CONTA RECEITA LANÇAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handlers para o campo de saldo
  const handleSaldoFocus = () => {
    setIsEditingSaldo(true);
  };

  const handleSaldoBlur = () => {
    setIsEditingSaldo(false);
    if (saldoClientesDiversos) {
      setSaldoClientesDiversos(formatarValorMonetario(saldoClientesDiversos));
    }
  };

  const handleSaldoChange = (e) => {
    const valor = e.target.value;
    if (isEditingSaldo) {
      setSaldoClientesDiversos(valor);
    } else {
      setSaldoClientesDiversos(formatarValorMonetario(valor));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Conta Receita - Zendry</h1>
              <p className="text-gray-600 mt-1">Análise de movimentações financeiras do sistema Zendry</p>
            </div>
          </div>
        </div>

        {/* Seleção de Tipo de Transação */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Tipo de Processamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Selecione o tipo de transação:</label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setTipoTransacao('credito')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      tipoTransacao === 'credito'
                        ? 'border-green-500 bg-green-50 shadow-md'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-bold text-lg text-gray-900">Créditos em Conta</p>
                      <p className="text-sm text-gray-600 mt-1">Processar entradas (receitas)</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setTipoTransacao('debito')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      tipoTransacao === 'debito'
                        ? 'border-red-500 bg-red-50 shadow-md'
                        : 'border-gray-300 hover:border-red-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-bold text-lg text-gray-900">Débitos em Conta</p>
                      <p className="text-sm text-gray-600 mt-1">Processar saídas (devoluções)</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Seleção de Banco - Apenas para Débitos */}
              {tipoTransacao === 'debito' && (
                <div className="mt-4 pt-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Selecione o banco de destino:</label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setBancoSelecionado('primepag')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        bancoSelecionado === 'primepag'
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-center">
                        <p className="font-bold text-lg text-gray-900">PrimePag</p>
                        <p className="text-sm text-gray-600 mt-1">Contas a Pagar</p>
                        <p className="text-xs text-gray-500 mt-2">Código: 18,16</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setBancoSelecionado('c6bank')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        bancoSelecionado === 'c6bank'
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      <div className="text-center">
                        <p className="font-bold text-lg text-gray-900">C6 Bank</p>
                        <p className="text-sm text-gray-600 mt-1">Conta Corrente</p>
                        <p className="text-xs text-gray-500 mt-2">Código: 19,16</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Selecionado:</strong> {tipoTransacao === 'credito' ? 'Créditos em Conta' : `Débitos em Conta - ${bancoSelecionado === 'primepag' ? 'PrimePag (18,16)' : 'C6 Bank (19,16)'}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Auditoria Prévia - CSV Bruto x OFX */}
        <Card className="mb-8 border-indigo-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-indigo-700">
              <FileText className="w-5 h-5" />
              Auditoria Prévia (CSV Bruto x OFX)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload OFX bruto</label>
                <div
                  className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400"
                  onClick={() => ofxPrevioInputRef.current?.click()}
                >
                  <p className="text-sm text-gray-600">Clique para selecionar o OFX</p>
                  {ofxPrevioData && (
                    <p className="text-xs text-indigo-700 mt-2">
                      {ofxPrevioData.transacoes.length} transações carregadas
                    </p>
                  )}
                </div>
                <input
                  ref={ofxPrevioInputRef}
                  type="file"
                  accept=".ofx"
                  onChange={(e) => handleOFXPrevioUpload(e.target.files[0])}
                  className="hidden"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV bruto do extrato</label>
                <div
                  className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400"
                  onClick={() => csvPrevioInputRef.current?.click()}
                >
                  <p className="text-sm text-gray-600">Clique para selecionar o CSV bruto</p>
                  {csvPrevioData && (
                    <p className="text-xs text-indigo-700 mt-2">
                      {csvPrevioData.transacoes.length} lançamentos carregados
                    </p>
                  )}
                </div>
                <input
                  ref={csvPrevioInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleCSVPrevioUpload(e.target.files[0])}
                  className="hidden"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={realizarConciliacaoPrevia}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
                disabled={!ofxPrevioData || !csvPrevioData}
              >
                <Download className="w-4 h-4" />
                Conciliar prévia
              </Button>
              <span className="text-sm text-gray-500 self-center">
                {(!ofxPrevioData || !csvPrevioData) ? 'Carregue OFX e CSV bruto para conciliar.' : 'Pronto para conciliar.'}
              </span>
            </div>

            {conciliacaoPrevia && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-indigo-50 border-indigo-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-indigo-700">Total OFX</p>
                      <p className="text-lg font-bold text-indigo-800">
                        {formatCurrency(conciliacaoPrevia.totalOFX)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-green-700">Total CSV bruto</p>
                      <p className="text-lg font-bold text-green-800">
                        {formatCurrency(conciliacaoPrevia.totalContabil)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-red-700">Diferença Total</p>
                      <p className="text-lg font-bold text-red-800">
                        {formatCurrency(conciliacaoPrevia.diferencaTotal)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-purple-700">Taxa de Conciliação</p>
                      <p className="text-lg font-bold text-purple-800">
                        {conciliacaoPrevia.taxaConciliacao.toFixed(1)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm text-indigo-700">Somente no OFX</CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => copiarDivergenciasPrevias('ofx')}
                          disabled={!conciliacaoPrevia.somenteOFX.length}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copiar
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                      {conciliacaoPrevia.somenteOFX.map((item, idx) => (
                        <div key={`previo-ofx-${idx}`} className="p-2 bg-indigo-50 rounded-lg text-xs">
                          <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                          <p className="text-gray-600">{item.memo}</p>
                        </div>
                      ))}
                      {!conciliacaoPrevia.somenteOFX.length && (
                        <p className="text-xs text-gray-500">Nenhuma divergência encontrada no OFX.</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm text-orange-700">Somente no CSV bruto</CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => copiarDivergenciasPrevias('contabil')}
                          disabled={!conciliacaoPrevia.somenteContabil.length}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copiar
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                      {conciliacaoPrevia.somenteContabil.map((item, idx) => (
                        <div key={`previo-contabil-${idx}`} className="p-2 bg-orange-50 rounded-lg text-xs">
                          <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                          <p className="text-gray-600">{item.historico}</p>
                        </div>
                      ))}
                      {!conciliacaoPrevia.somenteContabil.length && (
                        <p className="text-xs text-gray-500">Nenhuma divergência encontrada no CSV bruto.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Área de Upload */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload do Arquivo CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300 hover:border-green-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-green-100 rounded-full">
                  <FileText className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isProcessing ? 'Processando arquivo...' : 'Arraste e solte o arquivo CSV aqui'}
                  </p>
                  <p className="text-gray-500 mt-1">
                    ou clique para selecionar o arquivo
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  disabled={isProcessing}
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo
                </Button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Campo para Saldo da Conta Clientes Diversos */}
        {summary && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Configuração da Conta Clientes Diversos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Saldo da Conta Clientes Diversos (Limite para código 142)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      R$
                    </span>
                    <input
                      type="text"
                      value={saldoClientesDiversos}
                      onChange={handleSaldoChange}
                      onFocus={handleSaldoFocus}
                      onBlur={handleSaldoBlur}
                      placeholder="0,00"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Valores de crédito até este limite usarão o código 142. Acima deste valor usarão o código 2703.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resumo Financeiro */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Total Créditos</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.totalCredito)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Total Débitos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.totalDebito)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Saldo Final</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.saldoFinal)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Total Transações</p>
                    <p className="text-2xl font-bold text-gray-700">
                      {summary.totalTransacoes}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">Clientes Diversos (142)</p>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency(calcularClientesDiversos())}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-wrap gap-4 mb-8">
              <Button onClick={downloadJSON} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Baixar JSON
              </Button>
              <Button variant="outline" onClick={copyJSON} className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copiar JSON
              </Button>
              <Button 
                onClick={gerarCSVFormatado} 
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                disabled={!saldoClientesDiversos}
              >
                <Download className="w-4 h-4" />
                Baixar CSV Formatado
              </Button>
            </div>

            {/* Auditoria OFX x CSV Contábil */}
            <Card className="mb-8 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <FileText className="w-5 h-5" />
                  Auditoria OFX x Conta Contábil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload OFX (extrato bancário)</label>
                    <div
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400"
                      onClick={() => ofxInputRef.current?.click()}
                    >
                      <p className="text-sm text-gray-600">Clique para selecionar o OFX</p>
                      {ofxData && (
                        <p className="text-xs text-blue-700 mt-2">
                          {ofxData.transacoes.length} transações carregadas
                        </p>
                      )}
                    </div>
                    <input
                      ref={ofxInputRef}
                      type="file"
                      accept=".ofx"
                      onChange={(e) => handleOFXUpload(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV contábil</label>
                    <div
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400"
                      onClick={() => contabilInputRef.current?.click()}
                    >
                      <p className="text-sm text-gray-600">Clique para selecionar o CSV contábil</p>
                      {contabilData && (
                        <p className="text-xs text-blue-700 mt-2">
                          {contabilData.transacoes.length} lançamentos carregados
                        </p>
                      )}
                    </div>
                    <input
                      ref={contabilInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleContabilUpload(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={realizarConciliacao}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    disabled={!ofxData || !contabilData}
                  >
                    <Download className="w-4 h-4" />
                    Conciliar agora
                  </Button>
                  <span className="text-sm text-gray-500 self-center">
                    {(!ofxData || !contabilData) ? 'Carregue os dois arquivos para conciliar.' : 'Pronto para conciliar.'}
                  </span>
                </div>

                {conciliacaoResultado && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-blue-700">Total OFX</p>
                          <p className="text-lg font-bold text-blue-800">
                            {formatCurrency(conciliacaoResultado.totalOFX)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-green-700">Total Contábil</p>
                          <p className="text-lg font-bold text-green-800">
                            {formatCurrency(conciliacaoResultado.totalContabil)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-red-50 border-red-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-red-700">Diferença Total</p>
                          <p className="text-lg font-bold text-red-800">
                            {formatCurrency(conciliacaoResultado.diferencaTotal)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-purple-700">Taxa de Conciliação</p>
                          <p className="text-lg font-bold text-purple-800">
                            {conciliacaoResultado.taxaConciliacao.toFixed(1)}%
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm text-blue-700">Somente no OFX</CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => copiarDivergencias('ofx')}
                              disabled={!conciliacaoResultado.somenteOFX.length}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copiar
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                          {conciliacaoResultado.somenteOFX.map((item, idx) => (
                            <div key={`ofx-${idx}`} className="p-2 bg-blue-50 rounded-lg text-xs">
                              <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                              <p className="text-gray-600">{item.memo}</p>
                            </div>
                          ))}
                          {!conciliacaoResultado.somenteOFX.length && (
                            <p className="text-xs text-gray-500">Nenhuma divergência encontrada no OFX.</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm text-orange-700">Somente no Contábil</CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => copiarDivergencias('contabil')}
                              disabled={!conciliacaoResultado.somenteContabil.length}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copiar
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                          {conciliacaoResultado.somenteContabil.map((item, idx) => (
                            <div key={`contab-${idx}`} className="p-2 bg-orange-50 rounded-lg text-xs">
                              <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                              <p className="text-gray-600">{item.historico}</p>
                            </div>
                          ))}
                          {!conciliacaoResultado.somenteContabil.length && (
                            <p className="text-xs text-gray-500">Nenhuma divergência encontrada no contábil.</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resumo Detalhado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total por Cliente */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Total por Cliente (Debitado)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                  {(() => {
                    // Calcular total por cliente baseado na coluna "Debitado"
                    const totalPorCliente = csvData.reduce((acc, row) => {
                      if (row.debitado && row.debitado !== '---' && row.valor > 0) {
                        if (!acc[row.debitado]) {
                          acc[row.debitado] = 0;
                        }
                        acc[row.debitado] += row.valor;
                      }
                      return acc;
                    }, {});

                    // Ordenar por valor decrescente
                    const clientesOrdenados = Object.entries(totalPorCliente)
                      .sort(([,a], [,b]) => b - a);

                    return clientesOrdenados.map(([cliente, total], index) => (
                      <div key={cliente} className={`flex justify-between items-center p-3 rounded-lg ${
                        index % 2 === 0 ? 'bg-blue-50' : 'bg-gray-50'
                      }`}>
                        <span className={`font-medium text-sm ${
                          index % 2 === 0 ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          {cliente}
                        </span>
                        <span className={`font-bold ${
                          index % 2 === 0 ? 'text-blue-800' : 'text-gray-800'
                        }`}>
                          {formatCurrency(total)}
                        </span>
                      </div>
                    ));
                  })()}
                  {csvData.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-green-700 font-medium">Total de Clientes:</span>
                        <span className="text-green-800 font-bold">
                          {Object.keys(csvData.reduce((acc, row) => {
                            if (row.debitado && row.debitado !== '---' && row.valor > 0) {
                              acc[row.debitado] = true;
                            }
                            return acc;
                          }, {})).length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-700 font-medium">Valor Total Clientes:</span>
                        <span className="text-blue-800 font-bold">
                          {(() => {
                            const totalPorCliente = csvData.reduce((acc, row) => {
                              if (row.debitado && row.debitado !== '---' && row.valor > 0) {
                                if (!acc[row.debitado]) {
                                  acc[row.debitado] = 0;
                                }
                                acc[row.debitado] += row.valor;
                              }
                              return acc;
                            }, {});
                            
                            const valorTotalClientes = Object.values(totalPorCliente).reduce((sum, valor) => sum + valor, 0);
                            return formatCurrency(valorTotalClientes);
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Distribuição por Códigos de Conta */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Distribuição por Códigos de Conta
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <div>
                      <span className="text-green-700 font-medium">Código 142 (Clientes Diversos):</span>
                      <p className="text-sm text-green-600">Até o limite de {formatCurrency(converterValorParaNumero(saldoClientesDiversos))}</p>
                    </div>
                    <span className="text-green-800 font-bold">
                      {formatCurrency(calcularClientesDiversos())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <div>
                      <span className="text-orange-700 font-medium">Código 2703 (Excedente):</span>
                      <p className="text-sm text-orange-600">Valores acima do limite</p>
                    </div>
                    <span className="text-orange-800 font-bold">
                      {formatCurrency(summary.totalCredito - calcularClientesDiversos())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 font-medium">Percentual em Clientes Diversos:</span>
                    <span className="text-gray-800 font-bold">
                      {((calcularClientesDiversos() / summary.totalCredito) * 100).toFixed(1)}%
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-purple-700 font-medium">Transferência Para Contas a Pagar:</span>
                    <span className="text-purple-800 font-bold">
                      {(() => {
                        const transacoesDebito = csvData.filter(row => 
                          row.valor < 0 && 
                          row.creditado === 'ZENDRY' && 
                          row.debitado === 'ZENDRY S.A'
                        );
                        const totalDebito = transacoesDebito.reduce((sum, row) => sum + Math.abs(row.valor), 0);
                        return formatCurrency(totalDebito);
                      })()}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-700 font-medium">Transações Processadas para CSV:</span>
                    <span className="text-blue-800 font-bold">
                      {csvData.filter(row => row.valor > 0).length + csvData.filter(row => 
                        row.valor < 0 && 
                        row.creditado === 'ZENDRY' && 
                        row.debitado === 'ZENDRY S.A'
                      ).length} linhas
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
